import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { PrimaryButton } from '../components/PrimaryButton';
import { SecondaryButton } from '../components/SecondaryButton';
import { MainHeader } from '../components/MainHeader';
import { Footer } from '../components/Footer';
import {
  LocationAutocomplete,
  type SelectedPlace,
} from '../components/LocationAutocomplete';
import { getBookingDraft, patchBookingDraft } from '../../lib/booking-draft';
import { listCategories } from '../../lib/booking-api';

export default function ServicePlacePage() {
  const navigate = useNavigate();
  const [address, setAddress] = useState('');
  const [place, setPlace] = useState<SelectedPlace | null>(null);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(
    'Please enter your address',
  );
  // Route by the picked category's booking_flow rather than hard-
  // coding /select-treatment — otherwise a customer who picked
  // Cleaning on the landing grid would still land on the treatment
  // wizard. Falls back to /select-treatment to preserve historical
  // direct-nav behavior.
  const [nextPath, setNextPath] = useState<'/booking' | '/select-treatment'>(
    '/select-treatment',
  );
  const [categoryName, setCategoryName] = useState<string>('');

  useEffect(() => {
    const draft = getBookingDraft();
    const catId = draft?.service_category_id;
    if (!catId) return;
    let cancelled = false;
    listCategories()
      .then((res) => {
        if (cancelled) return;
        const cat = res.data.find((c) => c.id === catId);
        setCategoryName(cat?.name ?? '');
        if (cat?.booking_flow === 'SESSION') setNextPath('/booking');
        else setNextPath('/select-treatment');
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const handleNext = () => {
    if (!address) {
      setErrorMessage('Please enter your address');
      setError(true);
      return;
    }
    // Every booking has to carry real coordinates to the backend
    // (pro matcher, travel-radius filter, subscription cycle spawning
    // all read them) — and the API now rejects a coord-less booking.
    // Block here so the customer corrects it on this screen instead
    // of getting bounced back from a later step.
    if (place?.latitude == null || place?.longitude == null) {
      setErrorMessage(
        'Please pick your address from the suggestions so we can match nearby pros.',
      );
      setError(true);
      return;
    }
    // Snapshot the customer's address onto the draft so the
    // SelectProfessional grid can radius-filter, and so
    // ContinueBookingPage has it for the create call.
    patchBookingDraft({
      address: {
        address_line1: place.description ?? address,
        address_line2: null,
        city: place.city ?? null,
        postcode: place.postcode ?? null,
        country_code: place.country_code ?? null,
        latitude: place.latitude,
        longitude: place.longitude,
      },
    });
    navigate(nextPath);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <MainHeader />

      <div className="max-w-[1140px] mx-auto w-full px-4 md:px-8 pt-6">
        <SecondaryButton
          onClick={() => navigate(-1)}
          icon={<span className="material-icons text-[18px]">navigate_before</span>}
        >
          Back
        </SecondaryButton>
      </div>

      <div className="flex-1 max-w-[1140px] mx-auto px-4 md:px-8 pt-12 pb-20 w-full">
        {/* Title Section */}
        <div className="text-center mb-5">
          <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[25px] md:text-[34px] md:text-[40px] leading-[32px] md:leading-[54px] tracking-[-0.1px] mb-4">
            {categoryName
              ? `Where will your ${
                  // TREATMENT flow (Hair, Beauty & Wellness) reads better as
                  // "your appointment". SESSION (Cleaning / Childcare) keeps
                  // the category name.
                  nextPath === '/select-treatment'
                    ? 'appointment'
                    : categoryName.toLowerCase()
                } take place?`
              : (
                  <>
                    Where will the service take{' '}
                    <br className="hidden md:block" /> place?
                  </>
                )}
          </h1>
          <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-sm leading-[21px] tracking-[-0.1px] max-w-[430px] mx-auto" style={{ fontVariationSettings: "'opsz' 14" }}>
            To easily find your address, enter it in this order: number, street, city, postcode
          </p>
        </div>

        {/* Location Autocomplete */}
        <div className="max-w-[420px] mx-auto mb-8">
          <LocationAutocomplete
            label="Location"
            value={address}
            onChange={(value) => {
              setAddress(value);
              setError(false);
            }}
            onPlaceSelected={(p) => {
              setPlace(p);
              setAddress(p.description);
            }}
            error={error}
            errorMessage={errorMessage}
            className="w-full"
            containerHeight="h-[72px]"
            icon="location_on"
          />
        </div>

        {/* Next Button */}
        <div className="max-w-[420px] mx-auto">
          <PrimaryButton onClick={handleNext} className="w-full h-[54px]">
            Next
          </PrimaryButton>
        </div>
      </div>

      <Footer />
    </div>
  );
}
